import { Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import './Directory.css';

const DIRECTORY_API_URL =
  'https://script.google.com/macros/s/AKfycby4gkOUpbsIdF2xt6HJ6AB1B4TcWmelCvn37AP_xddGGFM51n5h9GxmXVfQIf-g9WHN/exec';

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export default function Directory() {
  const [query, setQuery] = useState('');
  const [activeLetter, setActiveLetter] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoText, setInfoText] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const runSearch = async (searchValue) => {
    const cleanedQuery = searchValue.trim();

    if (!cleanedQuery) {
      setError('Please enter a search term.');
      setInfoText('');
      setHeaders([]);
      setRows([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError('');
    setInfoText('');
    setHeaders([]);
    setRows([]);
    setHasSearched(true);
    setSortConfig({ key: null, direction: 'asc' });

    try {
      const url = `${DIRECTORY_API_URL}?q=${encodeURIComponent(cleanedQuery)}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      setInfoText(data?.[0]?.[0] || '');
      setHeaders(data?.[1] || []);
      setRows(data?.slice(2) || []);
    } catch (err) {
      setError('Something went wrong while retrieving directory information.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActiveLetter('');
    await runSearch(query);
  };

  const handleLetterSearch = async (letter) => {
    setQuery('');
    setActiveLetter(letter);

    setLoading(true);
    setError('');
    setInfoText('');
    setHeaders([]);
    setRows([]);
    setHasSearched(true);
    setSortConfig({ key: null, direction: 'asc' });

    try {
      const url = `${DIRECTORY_API_URL}?lastInitial=${encodeURIComponent(letter)}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      setInfoText(data?.[0]?.[0] || '');
      setHeaders(data?.[1] || []);
      setRows(data?.slice(2) || []);
    } catch (err) {
      setError('Something went wrong while retrieving directory information.');
    } finally {
      setLoading(false);
    }
  };
  const handleReset = () => {
    setQuery('');
    setActiveLetter('');
    setLoading(false);
    setInfoText('');
    setHeaders([]);
    setRows([]);
    setError('');
    setHasSearched(false);
    setSortConfig({ key: null, direction: 'asc' });
  };

  const getCellText = (cell) => {
    if (Array.isArray(cell)) {
      return cell
        .map((part) => {
          if (part && typeof part === 'object' && 'text' in part) {
            return part.text ?? '';
          }
          return '';
        })
        .join(' ')
        .trim();
    }

    return cell == null ? '' : String(cell).trim();
  };

  const parseSortableValue = (value) => {
    const text = value.trim();

    if (text === '') return '';

    const numberValue = Number(text.replace(/,/g, ''));
    if (!Number.isNaN(numberValue) && text !== '') {
      return numberValue;
    }

    return text.toLowerCase();
  };

  const handleSort = (columnIndex) => {
    setSortConfig((prev) => {
      if (prev.key === columnIndex) {
        return {
          key: columnIndex,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        key: columnIndex,
        direction: 'asc',
      };
    });
  };

  const sortedRows = useMemo(() => {
    if (sortConfig.key === null) return rows;

    const sorted = [...rows].sort((a, b) => {
      const aText = getCellText(a[sortConfig.key]);
      const bText = getCellText(b[sortConfig.key]);

      const aVal = parseSortableValue(aText);
      const bVal = parseSortableValue(bText);

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [rows, sortConfig]);

  /*
  const downloadCSV = () => {
    if (!headers.length) return;

    const escapeCSV = (value) => {
      const stringValue = String(value ?? '');
      return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const csvRows = [
      headers.map(escapeCSV).join(','),
      ...sortedRows.map((row) =>
        row.map((cell) => escapeCSV(getCellText(cell))).join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;

    const safeQuery =
      query.trim().replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'directory-search';
    link.download = `directory-${safeQuery}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };
  */
 
  const renderSortIcon = (index) => {
    if (sortConfig.key !== index) {
      return <ArrowUpDown size={14} className="directory-sort-icon" />;
    }

    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={14} className="directory-sort-icon active" />
    ) : (
      <ArrowDown size={14} className="directory-sort-icon active" />
    );
  };

  const renderCellContent = (cell) => {
    if (Array.isArray(cell)) {
      return cell.map((part, index) => {
        if (part && typeof part === 'object' && 'text' in part) {
          return part.url ? (
            <a
              key={index}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="directory-link"
            >
              {part.text}
            </a>
          ) : (
            <span key={index}>{part.text}</span>
          );
        }

        return null;
      });
    }

    if (typeof cell === 'string') {
      const trimmed = cell.trim();

      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        return (
          <a href={`mailto:${trimmed}`} className="directory-link">
            {trimmed}
          </a>
        );
      }
    }

    return cell ?? '';
  };

  return (
    <div className="directory-page min-h-screen bg-white">
      <div
        className="directory-hero"
        style={{ background: 'linear-gradient(135deg, #001f3f 0%, #003d66 100%)' }}
      >
        <div className="directory-hero-overlay">
          <div className="directory-orb directory-orb-one"></div>
          <div className="directory-orb directory-orb-two"></div>
        </div>

        <div className="directory-container directory-hero-content">


          <h1 className="font-playfair directory-hero-title">Directory</h1>
          <p className="font-poppins directory-hero-subtitle">
            Search the directory by any name, phone number, address, or other available field.
          </p>
          <p className="font-poppins directory-hero-info">
            <br />Confidential Notice <br /> <br />
            This directory data has been prepared exclusively for Members of Anjuman-e-Burhani NJ. The information contained herein is confidential and must not be shared, copied, or distributed.
            <br /> <br />
            Please note: Some names may be missing, as a few members have not provided their consent to be included. Respect for privacy is paramount, and no personal data should be disclosed without explicit permission.
          </p>
        </div>
      </div>

      <div className="directory-container directory-main">
        <div className="directory-shell">
          <div className="directory-form-card">
            <h2 className="directory-section-title">Search Directory</h2>

            <form onSubmit={handleSubmit} className="directory-form">
              <div className="directory-input-row">
                <div className="directory-input-wrap">
                  <Search className="directory-input-icon" size={18} />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setActiveLetter('');
                    }}
                    placeholder="Search by name, phone, address..."
                    className="directory-input"
                    required
                  />
                </div>

                <div className="directory-button-row">
                  <button
                    type="submit"
                    disabled={loading}
                    className="directory-submit-btn"
                  >
                    {loading ? 'Searching...' : 'Search'}
                  </button>

                  <button
                    type="button"
                    onClick={handleReset}
                    className="directory-reset-btn"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {error && <p className="directory-error">{error}</p>}
            </form>

            <div className="directory-alpha-wrap">
              <p className="directory-alpha-label">Browse by Last Name</p>

              <div className="directory-alpha-list" aria-label="Browse directory by last name initial">
                {alphabet.map((letter) => (
                  <button
                    key={letter}
                    type="button"
                    onClick={() => handleLetterSearch(letter)}
                    disabled={loading}
                    className={`directory-alpha-letter ${
                      activeLetter === letter ? 'active' : ''
                    }`}
                  >
                    {letter}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {(hasSearched || infoText || headers.length > 0 || rows.length > 0 || error) && (
            <div className="directory-results-card">
              {infoText && <div className="directory-info-text">{infoText}</div>}

              {/* removing csv trigger
              {headers.length > 0 && (
                <div className="directory-toolbar">
                  <button
                    type="button"
                    className="directory-export-btn"
                    onClick={downloadCSV}
                  >
                    <Download size={16} />
                    <span>Export CSV</span>
                  </button>
                </div>
              )}
              */}

              {headers.length > 0 && (
                <div className="directory-table-container">
                  <table className="directory-table">
                    <thead>
                      <tr>
                        {headers.map((header, index) => (
                          <th
                            key={index}
                            onClick={() => handleSort(index)}
                            className="directory-sortable-header"
                            scope="col"
                          >
                            <button type="button" className="directory-sort-button">
                              <span>{header}</span>
                              {renderSortIcon(index)}
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {sortedRows.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className={rowIndex % 2 === 0 ? 'row-white' : 'row-purple'}
                        >
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex}>{renderCellContent(cell)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {hasSearched && !loading && !error && headers.length === 0 && rows.length === 0 && (
                <div className="directory-empty-state">No results to display.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
